import { usePrincing } from '@/steps/save-booking/pricing';
import { useEmployeesStore } from '@/stores/employees';
import { useServicesStore } from '@/stores/services';
import type {
  BookingResponse,
  DayPeriods,
  DaysOfWeek,
  DiscountCode,
  GiftcardCode,
  PaymentMethodOptions,
  Pricing,
  Service,
  ServiceVariation
} from '@/types';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

export const useBookingStore = defineStore('booking', () => {
  const locationId = ref<string>();
  const date = ref<string>('');
  const time = ref<string>('');
  const pricing = ref<Pricing | null>(null);
  const paymentMethod = ref<PaymentMethodOptions>('pay_at_location');

  const timeSlotPriceAdjustment = ref<number>(0);

  const discountCode = ref<DiscountCode>();
  const giftcardCode = ref<GiftcardCode>();
  const giftcardRedeemedAmount = ref<number>(0);

  const priceAdjustment = computed(
    () => timeSlotPriceAdjustment.value || discountCode.value?.percentage || 0
  );

  // Reset the time when the date changes
  watch(date, () => (time.value = ''));

  // Reset the discount and giftcard code when the time changes
  watch(time, () => {
    discountCode.value = undefined;
    giftcardCode.value = undefined;
    giftcardRedeemedAmount.value = 0;
  });

  const employeeId = ref<number | null>(null);
  const employee = computed(() => {
    const { employees } = useEmployeesStore();
    return employees.find((employee) => employee.id === employeeId.value);
  });

  const serviceIds = ref<number[]>([]);
  const services = computed(() => {
    const { services } = useServicesStore();
    // Doing a map on serviceIds rather than a filter on services, because the services array likely contains duplicate services, due to the "top services"
    return serviceIds.value.map((id) =>
      services.find((service) => service.id === id)
    ) as Service[] | ServiceVariation[];
  });
  const totalDuration = computed(() =>
    services.value.length
      ? services.value
          .map((service) => service?.duration || 0)
          .reduce((a, b) => a + b)
      : 0
  );

  const subTotalPrice = computed(() =>
    services.value.length
      ? services.value
          .map((service) => service?.price || 0)
          .reduce((a, b) => a + b)
      : 0
  );
  const totalPrice = computed(
    () => (subTotalPrice.value * (100 + priceAdjustment.value)) / 100
  );

  // The giftcard amount when using the giftcard widget
  const giftcardAmount = ref(0);

  // The response of the booking, needed to show details on the last confirmation page
  const bookingResponse = ref<BookingResponse>();

  // Wether or not the user is signing up the waiting list
  const waitingList = ref(false);

  const daysOfWeek = ref<DaysOfWeek[]>([]);
  const partsOfDay = ref<DayPeriods[]>([]);

  const resetDate = () => {
    date.value = '';
    time.value = '';
  };

  return {
    locationId,
    pricing,
    paymentMethod,
    serviceIds,
    employeeId,
    employee,
    date,
    time,
    services,
    totalDuration,
    subTotalPrice,
    totalPrice,
    discountCode,
    giftcardCode,
    giftcardRedeemedAmount,
    timeSlotPriceAdjustment,
    priceAdjustment,
    giftcardAmount,
    bookingResponse,
    waitingList,
    daysOfWeek,
    partsOfDay,
    resetDate
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBookingStore, import.meta.hot));
}
